/**
 * Sportconnect — Cart drawer Web Component
 * Snazzy Digital Agency
 *
 * Mini-panier AJAX :
 * - Ouverture sur event "sc:cart:open" (déclenché par le header)
 * - Ajout / suppression / mise à jour de quantité via Cart API
 * - Diffuse "sc:cart:updated" avec le nouveau cart.item_count
 *   pour la mise à jour du compteur du header
 */

class SCCartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-cart-drawer]');
    this.itemsContainer = this.querySelector('[data-cart-items]');

    document.addEventListener('sc:cart:open', () => this.open());
    document.addEventListener('sc:cart:added', async () => {
      await this.refresh();
      this.open();
    });

    this.querySelectorAll('[data-cart-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });

    this.bindLines();
  }

  open() {
    if (!this.dialog) return;
    this.dialog.showModal();
    this.attachSwipeToClose();
  }

  attachSwipeToClose() {
    if (this._swipeAttached) return;
    const dialog = this.dialog;
    if (!dialog) return;
    const handle = dialog.querySelector('[data-drag-handle]');
    if (!handle) return;
    const isMobile = () => window.matchMedia('(max-width: 767.98px)').matches;
    let startY = 0, currentY = 0, dragging = false;
    const onStart = (e) => {
      if (!isMobile()) return;
      const touch = e.touches ? e.touches[0] : e;
      startY = touch.clientY;
      currentY = 0;
      dragging = true;
      dialog.style.transition = 'none';
    };
    const onMove = (e) => {
      if (!dragging) return;
      const touch = e.touches ? e.touches[0] : e;
      currentY = Math.max(0, touch.clientY - startY);
      dialog.style.transform = 'translateY(' + currentY + 'px)';
    };
    const onEnd = () => {
      if (!dragging) return;
      dragging = false;
      dialog.style.transition = '';
      dialog.style.transform = '';
      if (currentY > 60) this.close();
    };
    handle.addEventListener('touchstart', onStart, { passive: true });
    handle.addEventListener('touchmove', onMove, { passive: true });
    handle.addEventListener('touchend', onEnd);
    handle.addEventListener('touchcancel', onEnd);
    this._swipeAttached = true;
  }
  close() {
    if (!this.dialog) return;
    this.dialog.dataset.closing = 'true';
    setTimeout(() => {
      if (this.dialog) {
        this.dialog.close();
        delete this.dialog.dataset.closing;
      }
    }, 450);
  }

  bindLines() {
    this.querySelectorAll('[data-line-item]').forEach((line) => {
      const key = line.dataset.lineKey;
      const qtyInput = line.querySelector('[data-line-qty]');
      const inc = line.querySelector('[data-line-increment]');
      const dec = line.querySelector('[data-line-decrement]');
      const rm = line.querySelector('[data-line-remove]');

      inc?.addEventListener('click', () => this.changeQty(key, parseInt(qtyInput.value, 10) + 1));
      dec?.addEventListener('click', () => this.changeQty(key, Math.max(0, parseInt(qtyInput.value, 10) - 1)));
      qtyInput?.addEventListener('change', () => this.changeQty(key, parseInt(qtyInput.value, 10)));
      rm?.addEventListener('click', () => this.changeQty(key, 0));
    });
  }

  async changeQty(lineKey, quantity) {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity }),
      });
      const cart = await res.json();
      document.dispatchEvent(new CustomEvent('sc:cart:updated', { detail: cart }));
      await this.refresh();
    } catch (err) {
      console.error('Cart update failed', err);
    }
  }

  async refresh() {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=cart-drawer`);
      const html = await res.text();
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const newDialog = tmp.querySelector('[data-cart-drawer]');
      if (newDialog && this.dialog) {
        const wasOpen = this.dialog.open;
        this.dialog.innerHTML = newDialog.innerHTML;
        this.itemsContainer = this.querySelector('[data-cart-items]');
        this.bindLines();
        if (wasOpen) this.open();
      }
    } catch (err) {
      console.error('Cart refresh failed', err);
    }
  }
}

if (!customElements.get('sc-cart-drawer')) {
  customElements.define('sc-cart-drawer', SCCartDrawer);
}
